import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { NMObjectUploadPopup } from "@neumetric/blueprint-lib/flows";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  getDepartments,
  getCurrentUser,
  isUser,
} from "@neumetric/blueprint-lib/store";
import {
  Col,
  Row,
  Button,
  TextButton,
  NMMenu,
  NMForm,
  NMFormPopup,
  NMPopup,
  Input,
  SelectedFileCard,
  OutlinedButton,
} from "@neumetric/blueprint-lib/components";
import { DotIcon } from "@neumetric/blueprint-lib/icons";
import {
  auditActions,
  getCurrentAudit,
  getCurrentControl,
  getPriorities,
  getPrioritiesByScore,
  canUpdateAudit,
  isAuditSubmitted,
  isAuditCompleted,
} from "../../store";

const STEPS = [
  "Download the audit file provided below",
  "Update & correct the required data in the downloaded .csv file.",
  "Once the corrections are completed, re-import the updated CSV file into the system.",
];

const items = [
  {
    label: "Export Audit",
    id: "export",
    variant: "ACTION",
    shouldCloseOnClick: true,
    classes: { item: "fw-400" },
  },
  {
    label: "Import Audit",
    variant: "ACTION",
    id: "import",
    shouldCloseOnClick: true,
  },
  {
    label: "Import Status",
    variant: "ACTION",
    id: "importStatus",
    shouldCloseOnClick: true,
  },
  {
    label: "Gap Details",
    variant: "ACTION",
    id: "gapDetails",
    shouldCloseOnClick: true,
  },
  {
    label: "Report",
    variant: "ACTION",
    id: "report",
    shouldCloseOnClick: true,
  },
];

const InfoPopupFields = [
  {
    attribute: "desc",
    typeRepresentation: "TEXT",
    placeholder: "Description",
    label: "Description:",
    readOnly: true,
    classes: { root: "w-100 mb-10", label: "w-115-px", input: "bg-contrast" },
  },
  {
    attribute: "start_date",
    typeRepresentation: "DATE",
    label: "Start Date:",
    readOnly: true,
    format: "ddd, DD-MMM-YYYY",
    labelAlign: "middle",
    classes: { root: "w-100 mb-10", label: "w-103-px" },
  },
  {
    attribute: "end_date",
    typeRepresentation: "DATE",
    label: "End Date:",
    readOnly: true,
    format: "ddd, DD-MMM-YYYY",
    labelAlign: "middle",
    classes: { root: "w-100 mb-10", label: "w-103-px" },
  },
];

const FilterForms = [
  {
    label: "Department:",
    attribute: "department",
    typeRepresentation: "DROPDOWN",
    placeholder: "Department",
    layout: "vertical",
    allowClear: true,
    classes: { root: "w-19 mb-0" },
  },
  {
    label: "Priority:",
    attribute: "priority",
    typeRepresentation: "DROPDOWN",
    placeholder: "Priority",
    layout: "vertical",
    classes: { root: "w-17 mb-0" },
    allowClear: true,
    template: "DOT",
  },
  {
    label: "Score:",
    attribute: "score",
    typeRepresentation: "DROPDOWN",
    placeholder: "Score",
    layout: "vertical",
    classes: { root: "w-19 mb-0" },
    allowClear: true,
  },
  {
    label: "Status:",
    attribute: "status",
    typeRepresentation: "DROPDOWN",
    placeholder: "Status",
    layout: "vertical",
    classes: { root: "w-19 mb-0" },
    allowClear: true,
  },
  {
    label: "Response:",
    attribute: "response",
    typeRepresentation: "DROPDOWN",
    placeholder: "Response",
    layout: "vertical",
    classes: { root: "w-22 mb-0" },
    allowClear: true,
  },
];

const filterAttributeMap = {
  department: (c) => c.dept_name,
  priority: (c) => c.priority,
  score: (c) => c.risk_score,
  status: (c) => c.status,
  response: (c) => c.result_value,
};

export const AuditHeader = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(getCurrentUser);
  const { asmtId } = useParams();
  const { filteredControls } = props;
  const _filterRef = useRef(null);
  const [state, setState] = useState({
    showDocumentInfo: false,
    showDialog: "",
    dialogProps: null,
    submitting: false,
    uploadResult: null,
    filterOrder: [],
  });
  const departments = useSelector(getDepartments);
  const currentAudit = useSelector(getCurrentAudit);
  const currentControl = useSelector(getCurrentControl);
  const canEdit = useSelector(canUpdateAudit);
  const isSubmitted = useSelector(isAuditSubmitted);
  const isCompleted = useSelector(isAuditCompleted);

  const updateState = (iNewState) => {
    setState((_) => ({ ..._, ...(iNewState || {}) }));
  };

  const filterCount = useMemo(() => {
    const counts = {
      department: {},
      priority: {},
      score: {},
      status: {},
      response: {},
    };
    const controls = currentAudit.asmt_cntrls;
    const applyFilters = (data, filters) =>
      data.filter((c) =>
        filters.every((f) => {
          const value = filterAttributeMap[f.attribute](c);
          return f.values.includes(value);
        }),
      );

    const activeFilters = state.filterOrder.map((attribute) => ({
      attribute,
      values: _filterRef.current?.getValues()?.[attribute] || [],
    }));

    Object.keys(counts).forEach((attribute) => {
      const attributeIndex = state.filterOrder.indexOf(attribute);

      const prevFilters =
        attributeIndex === -1
          ? activeFilters
          : activeFilters.slice(0, attributeIndex);

      const filtered = applyFilters(controls, prevFilters);

      filtered.forEach((control) => {
        const value = filterAttributeMap[attribute](control);
        counts[attribute][value] = (counts[attribute][value] || 0) + 1;
      });
    });
    return counts;
  }, [filteredControls, state.filterOrder, currentAudit.asmt_cntrls]);

  const getOptions = useCallback(
    (attribute) => {
      const config = {
        department: {
          source: departments,
          count: filterCount.department,
        },
        priority: {
          source: getPriorities(),
          count: filterCount.priority,
        },
        response: {
          source: currentAudit.resultOptions,
          count: filterCount.response,
        },
        score: {
          source: getPrioritiesByScore(),
          count: filterCount.score,
        },
        status: {
          source: [
            { id: "Pending", label: "Pending" },
            { id: "Responded", label: "Responded" },
            { id: "Evaluated", label: "Evaluated" },
          ],
          count: filterCount.status,
        },
      };

      const current = config[attribute];
      if (!current) return [];

      const { source, count = {} } = current;

      return source
        .filter(({ id }) => (count[id] || 0) > 0)
        .map(({ id, label, dotIconName }) => {
          const value = count[id] || 0;
          return {
            id,
            label: `${label}${value > 0 ? ` (${value})` : ""}`,
            dotIconName: dotIconName,
          };
        });
    },
    [filterCount],
  );

  const handleChange = useCallback(
    (attribute, value) => {
      const filterOrder = [...state.filterOrder];
      const attibuteIndex = filterOrder.findIndex((attr) => attr === attribute);
      if (value && attibuteIndex === -1) {
        filterOrder.push(attribute);
      } else if (!value && attibuteIndex > -1) {
        filterOrder.splice(attibuteIndex, 1);
      }
      updateState({ filterOrder: filterOrder });
      const values = _filterRef.current.getValues();
      props.onFilterChange(values);
    },
    [state.filterOrder, props.onFilterChange],
  );

  const handleDocumentInfoClick = useCallback(() => {
    updateState({ showDocumentInfo: !state.showDocumentInfo });
  }, [state.showDocumentInfo]);

  const handleIncludeAllInReportConfirm = () => {
    updateState({ showDialog: null });
    dispatch(
      auditActions.updateIncludeReport({
        controlId: currentControl.id,
        auditId: currentAudit.id,
      }),
    );
  };

  const handleMenuClick = (item) => {
    if (item.id === "importStatus") {
      navigate(`/A/T/${asmtId}/import-status`);
      return;
    }
    if (item.id === "report") {
      navigate(`/A/T/${asmtId}/report`);
      return;
    }
    updateState({ showDialog: item.id });
  };

  const handleDownloadTemplate = useCallback(() => {
    updateState({ submitting: true });
    const filename = `${currentAudit.audit_id}_${currentAudit.title.replace(/\s+/g, "_")}.csv`;
    const action = auditActions.downloadTemplate({
      auditId: currentAudit.id,
      filename,
    });
    action.onComplete = () => {
      updateState({ submitting: false });
    };
    dispatch(action);
  }, [currentAudit]);

  const getSubmitDisabled = useCallback(() => {
    if (!isSubmitted && !isCompleted) {
      return currentAudit.summary.total !== currentAudit.summary.responded;
    }
    return currentAudit.summary.total !== currentAudit.summary.evaluated;
  }, [currentAudit, currentUser]);

  const handleExportConfirm = useCallback(() => {
    updateState({ submitting: true });
    const filename = `${currentAudit.audit_id}_${currentAudit.title.replace(/\s+/g, "_")}.csv`;
    const action = auditActions.exportToCSV({
      auditId: currentAudit.id,
      filename,
    });
    action.onComplete = () => {
      updateState({ submitting: false, showDialog: null });
    };
    dispatch(action);
  }, [currentAudit]);

  const handleDownloadResult = (res) => {
    if (res && res.id) {
      const action = auditActions.downloadUploadHistory({
        id: res.id,
        filename: res.filename,
      });
      dispatch(action);
    }
  };

  const handleUploadConfirm = (files) => {
    updateState({ submitting: true });
    const action = auditActions.uploadAudit({
      auditId: currentAudit.id,
      file: files[0],
    });
    action.onComplete = (res) => {
      res.onDownloadResult = handleDownloadResult.bind(null, res);
      updateState({ uploadResult: res, submitting: false });
    };
    dispatch(action);
  };

  const handleAuditSubmit = useCallback(() => {
    updateState({ submitting: true });
    const action = auditActions.submitAudit({ auditId: currentAudit.id });
    action.onComplete = () => {
      updateState({ submitting: false, showDialog: null });
    };
    dispatch(action);
  }, [currentAudit]);

  const menuItems = useMemo(() => {
    return items.filter((item) => {
      if (item.id === "import") {
        return canEdit;
      } else if (item.id === "report") {
        return currentAudit.lib1_model.report_format === "SOC 2 Type 2";
      }
      return true;
    });
  }, [canEdit]);

  const dialogConfig = useMemo(() => {
    if (state.showDialog === "IncludeAllControl") {
      return {
        width: "631px",
        title: "Report ALL Controls?",
        variant: "INFO",
        actions: [
          {
            btnType: "OUTLINED",
            icon: "CHECK_CONFIRM",
            label: "Confirm",
            onClick: handleIncludeAllInReportConfirm,
          },
          {
            btnType: "CONTAINED",
            icon: "CANCEL",
            label: "Cancel",
            onClick: () => updateState({ showDialog: null }),
          },
        ],
        children: (
          <Col className="fs-15 fw-400">
            This action will include all Controls in the Audit Report. <br />
            <br />
            <span>
              - <span className="fw-600">'Report this Control'</span> parameter
              for each Control in this Audit will be changed to{" "}
              <span className="fw-600">'True'</span>.
            </span>
            <span>
              - Audit Report with all Controls included are quite large in
              Filesize & Page Count.
            </span>
            <span>
              - Compliant Controls are listed first & then in order of Gap
              Severity.
            </span>
            <br />
            <span>
              Including ALL Controls is recommended ONLY for Gap Audits.
            </span>
            <br />
            <span className="fs-12">
              <span className="text-caution pr-2">CAUTION!</span> This action is
              difficult to undo. <br />
              To remove any specific Control from being included in the Audit
              Report, uncheck the 'Report this Control' parameter & then press
              Update.
            </span>
          </Col>
        ),
      };
    }
    if (state.showDialog === "export") {
      return {
        width: "420px",
        title: "Export Audit?",
        variant: "INFO",
        classes: { header: "pt-8 pb-8" },
        actions: [
          {
            btnType: "OUTLINED",
            disabled: state.submitting,
            icon: "CANCEL",
            label: "Cancel",
            onClick: () => updateState({ showDialog: null }),
          },
          {
            btnType: "CONTAINED",
            icon: "DOWNLOAD",
            label: "Export",
            loading: state.submitting,
            onClick: handleExportConfirm,
          },
        ],
        children: (
          <Col className="w-100">
            <span className="fs-15 fw-400 text-filled">
              This will export the Audit details in CSV format.
            </span>
          </Col>
        ),
      };
    }
    if (state.showDialog === "gapDetails") {
      return {
        width: "520px",
        title: (
          <span className="fs-15 text-filled fw-400">
            {currentAudit.title} Gap Details
          </span>
        ),
        variant: "INFO",
        showClose: "label",
        classes: { header: "pt-8 pb-8" },
        onModalClose: () => updateState({ showDialog: null }),
        children: (
          <>
            <Row className="w-100 h-32-px fs-16 text-filled" align="center">
              <span className="w-100-px">Gap Score:</span>
              <span>{currentAudit.gap_score}</span>
            </Row>
            <Row
              className="w-100 h-32-px fs-16 text-filled mb-16"
              align="center"
            >
              <span className="w-100-px">Gap Severity:</span>
              {currentAudit.gap_serverity ? (
                <Row align="center ml-1">
                  <DotIcon
                    name={currentAudit.gap_serverity.toLowerCase()}
                    className="mr-4"
                  />
                  {currentAudit.gap_serverity}
                </Row>
              ) : (
                <span>-</span>
              )}
            </Row>
          </>
        ),
      };
    }
    if (state.showDialog === "submit") {
      return {
        width: "480px",
        title: "Submit Audit?",
        variant: "CAUTION",
        actions: [
          {
            btnType: "TEXT",
            disabled: state.submitting,
            variant: "CAUTION",
            icon: "CANCEL",
            label: "Cancel",
            onClick: () => updateState({ showDialog: null }),
          },
          {
            btnType: "CONTAINED",
            icon: "CHECK_CONFIRM",
            label: "Confirm",
            loading: state.submitting,
            onClick: handleAuditSubmit,
          },
        ],
        children: (
          <Col className="w-100">
            <span className="fs-15 fw-400 text-filled mb-20">
              {!isSubmitted && !isCompleted
                ? "This action will submit the Audit for evaluation purpose."
                : "This action will submit the complete Audit & Generate the Report"}
            </span>
            <span className="fs-15 fw-400 text-filled mb-20">
              {!isSubmitted && !isCompleted
                ? "Further changes to Controls WILL NOT be possible."
                : "Further changes to Audit WILL NOT be possible."}
            </span>
            <span className="fs-15 fw-400 text-filled">Are you sure?</span>
          </Col>
        ),
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.showDialog,
    state.submitting,
    currentAudit,
    isSubmitted,
    isCompleted,
  ]);

  return (
    <Col className="w-100 card-shadow pl-20 pr-20 pb-10 bg-white">
      <Link className="mt-4 mb-12" to={"/A/T"}>
        <span className="fs-14 fw-400 text-action">Tracker</span>
      </Link>
      <Row className="w-100">
        <Col className="flex-auto">
          <Row className="w-100" justify="between" align="center">
            <Row align="center">
              <span className="fs-20 fw-600 text-heading">
                {currentAudit.title}
              </span>
              <TextButton
                icon="DOCUMENT_INFO"
                classes={{ icon: "fs-20", button: "ml-12" }}
                onClick={handleDocumentInfoClick}
              />
            </Row>
            <Row align="center">
              {
                <Button
                  label="Submit"
                  icon="SUBMIT"
                  disabled={getSubmitDisabled() || !canEdit}
                  classes={{ button: "mr-25" }}
                  onClick={() =>
                    canEdit && updateState({ showDialog: "submit" })
                  }
                />
              }
              {!isUser(currentUser) && (
                <NMMenu
                  placement="bottomRight"
                  showArrow={true}
                  items={menuItems}
                  onMenuClick={handleMenuClick}
                  headerButton={{
                    btnType: "OUTLINED",
                    icon: "DOT_MENU",
                    classes: { icon: "fs-3 mt-10" },
                  }}
                />
              )}
            </Row>
          </Row>
          <Row className="w-100 mt-12" justify="between" align="end">
            <img
              src={`${currentAudit.logo}`}
              alt=""
              className="w-108-px h-64-px mr-95"
            />
            <NMForm
              ref={_filterRef}
              fields={FilterForms}
              layout="vertical"
              formData={{}}
              getOptions={getOptions}
              onChange={handleChange}
            />
          </Row>
        </Col>
        <Row align="start" className="ml-25">
          <Col className="mr-40">
            <Row className="fs-18 fw-400 h-37-px" align="center">
              <span className="w-[11rem] ">Controls:</span>
              <span className="fw-600 text-filled">
                {currentAudit.summary.total}
              </span>
            </Row>
            <Row align="center" className="h-37-px">
              <Row className="fs-18 fw-400 " align="center">
                <span className="w-[11rem] ">Responded:</span>
                <span className="fw-600 text-closed">
                  {currentAudit.summary.responded}
                </span>
              </Row>
              {!isUser(currentUser) && (
                <Row className="fs-18 fw-400 ml-20" align="center">
                  <span className="w-[10rem]">Evaluated:</span>
                  <span className="fw-600 text-closed">
                    {currentAudit.summary.evaluated}
                  </span>
                </Row>
              )}
            </Row>
            <Row className="fs-18 fw-400 h-37-px" align="center">
              <span className="w-[11rem]">Pending:</span>
              <span className="fw-600 text-caution">
                {currentAudit.summary.pending}
              </span>
            </Row>
          </Col>
          {!isUser(currentUser) && (
            <Button
              label="Report All Controls"
              icon="ASSIGN"
              size="small"
              disabled={!canEdit}
              classes={{ icon: "fs-12" }}
              onClick={() =>
                canEdit && updateState({ showDialog: "IncludeAllControl" })
              }
            />
          )}
        </Row>
        {state.showDocumentInfo && (
          <NMFormPopup
            visible={true}
            width="814px"
            showClose="label"
            getOptions={getOptions}
            title={
              <span className="fs-15 text-filled fw-500">
                {currentAudit.title}
              </span>
            }
            classes={{ header: "bg-header pt-8 pb-8", content: "pt-12" }}
            fields={InfoPopupFields}
            formData={currentAudit}
            onModalClose={handleDocumentInfoClick}
          />
        )}
        {state.showDialog && dialogConfig && (
          <NMPopup visible={true} {...dialogConfig}></NMPopup>
        )}
        <NMObjectUploadPopup
          width="480px"
          key={state.showDialog === "import" ? "open" : "closed"}
          visible={state.showDialog === "import"}
          onModalClose={() =>
            updateState({ showDialog: null, uploadResult: null })
          }
          onUploadConfirm={handleUploadConfirm}
          isSubmitting={state.submitting}
          result={state.uploadResult}
          steps={STEPS}
          template={{
            onDownload: handleDownloadTemplate,
            downloading: state.submitting,
            name: `${currentAudit.audit_id}: ${currentAudit.title}`,
          }}
          accept=".csv,.xls,.xlsx"
          multiple={false}
          title="Update Audit"
          maskClosable={true}
          objectName="Control"
          description={
            <span className="fs-12 fw-400 text-filled mt-12">
              Note: Do not modify the GUID & ControlID columns. These fields are
              used as reference identifiers for accurate audit mapping during
              the import process.
            </span>
          }
        />
      </Row>
    </Col>
  );
};

export const Evidence = (props) => {
  const dispatch = useDispatch();
  const canEdit = useSelector(canUpdateAudit);
  const currentUser = useSelector(getCurrentUser);
  const currentControl = useSelector(getCurrentControl);
  const currentAudit = useSelector(getCurrentAudit);
  const [state, setState] = useState({
    showAttachEvidence: false,
    attachedFile: null,
    submitting: false,
    deletingFile: null,
    evidence_list: currentAudit.evidence_list || [],
  });
  const evidences = currentControl ? currentControl.evidences || [] : [];

  const updateState = (iNewState) => {
    setState((_) => ({ ..._, ...(iNewState || {}) }));
  };

  const handleAttachEvidence = useCallback(() => {
    if (!canEdit) {
      return;
    }
    updateState({ showAttachEvidence: true, attachedFile: null });
  }, [canEdit]);

  const handleUploadEvidenceConfirm = useCallback(() => {
    const attachedFile = state.attachedFile;
    if (!attachedFile || !canEdit) return;
    const action = auditActions.addControlComment({
      controlId: currentControl.id,
      evidence: attachedFile,
    });
    updateState({ submitting: true });
    action.onComplete = (newEvidence) => {
      const evidence_list = [...state.evidence_list];
      if (newEvidence) {
        evidence_list.push(newEvidence);
      }
      updateState({
        submitting: false,
        showAttachEvidence: false,
        attachedFile: null,
        evidence_list,
      });
    };
    action.onFailure = () => {
      updateState({ submitting: false });
    };
    dispatch(action);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.attachedFile, canEdit, currentControl, state.evidence_list]);

  const handleDeleteEvidenceConfirm = useCallback(() => {
    if (!canEdit) {
      return;
    }
    const action = auditActions.deleteEvidence({
      controlId: currentControl.id,
      evidenceId: state.deletingFile.id,
    });
    updateState({ submitting: true });
    action.onComplete = () => {
      updateState({ submitting: false, deletingFile: null });
    };
    dispatch(action);
  }, [state, canEdit]);

  const getEvidenceActions = useCallback(() => {
    return [
      {
        btnType: "OUTLINED",
        icon: "CANCEL",
        label: "Close",
        onClick: () => updateState({ showAttachEvidence: false }),
      },
      {
        btnType: "CONTAINED",
        icon: "UPLOAD",
        label: "Upload",
        disabled: !state.attachedFile,
        loading: state.submitting,
        onClick: handleUploadEvidenceConfirm,
      },
    ];
  }, [state]);

  const getDeleteEvidenceActions = useCallback(() => {
    return [
      {
        btnType: "TEXT",
        icon: "DELETE",
        variant: "CAUTION",
        label: "Delete",
        loading: state.submitting,
        onClick: handleDeleteEvidenceConfirm,
      },
      {
        btnType: "CONTAINED",
        icon: "CANCEL",
        label: "Cancel",
        onClick: () => updateState({ deletingFile: null }),
      },
    ];
  }, [state]);

  const handleEvidenceChange = (iFile) => {
    updateState({ attachedFile: iFile[0] });
  };

  const handleRemoveEvidence = () => {
    updateState({ attachedFile: null });
  };

  const getEvidenceOptions = useCallback(() => {
    return state.evidence_list || [];
  }, [state.evidence_list]);

  const handleEvidenceSelect = useCallback(
    (value) => {
      if (!canEdit) {
        return;
      }
      const action = auditActions.selectEvidence({
        controlId: currentControl.id,
        evidenceId: value,
      });
      dispatch(action);
    },
    [canEdit, currentControl],
  );

  return (
    <Fragment>
      <Col className="flex-auto w-100 border overflow-hide">
        <Col className="w-100 h-100">
          <Col className="w-100 shadow h-28-px pl-20" justify="center">
            <span>Evidence</span>
          </Col>
          <Col className="w-100 flex-auto overflow-auto">
            {evidences.length > 0 ? (
              evidences.map((evidence) => {
                return (
                  <Col key={evidence.id} className="w-100 pl-20 pr-20 mt-8">
                    <Row
                      align="center"
                      justify="between"
                      className=" w-100 fs-12 fw-400 text-subtitle"
                    >
                      <span>{evidence.creator.name}</span>
                      <span>{evidence.created_at}</span>
                    </Row>
                    {evidence.is_deleted ? (
                      <Col className="w-100 fs-12 fw-400 text-subtitle">
                        <Row align="center" className="w-100" justify="between">
                          <span>File deleted by {evidence.updater.name}</span>
                          <span>{evidence.updated_at}</span>
                        </Row>
                        <span>
                          [Deleted]{" "}
                          <span className="strike">
                            {evidence.evidence_file_name}
                          </span>
                        </span>
                      </Col>
                    ) : (
                      <Row className="w-100" align="center" justify="between">
                        <span className="text-action fs-14 fw-400">
                          {evidence.evidence_file_name}
                        </span>
                        {!isUser(currentUser) && canEdit && (
                          <TextButton
                            icon="DELETE"
                            variant="CAUTION"
                            size="small"
                            classes={{ button: "pr-0", icon: "fs-12" }}
                            onClick={() =>
                              updateState({ deletingFile: evidence })
                            }
                          />
                        )}
                      </Row>
                    )}
                  </Col>
                );
              })
            ) : (
              <Col align="center" justify="center" className="w-100 h-100">
                <span className="fs-14 ff-exo2 fw-400 text-subtitle">
                  No evidences
                </span>
              </Col>
            )}
          </Col>
          <Col className="w-100 pl-20 pr-20 pt-10 pb-10 border">
            <Row className="w-100 border">
              <Col
                className="fs-15 fw-400 flex-auto h-100 text-placeholder pl-12"
                justify="center"
              >
                Attach New Evidence
              </Col>
              <Button
                label="Attach"
                icon="ATTACHMENT"
                size="small"
                disabled={!canEdit}
                classes={{ icon: "fs-12" }}
                onClick={handleAttachEvidence}
              />
            </Row>
            <Input
              disabled={!canEdit}
              typeRepresentation="DROPDOWN"
              placeholder="Select Uploaded Evidence"
              classes={{ root: "w-100 mb-4" }}
              options={getEvidenceOptions()}
              onChange={handleEvidenceSelect}
            />
          </Col>
        </Col>
      </Col>

      {state.showAttachEvidence && (
        <NMPopup
          visible={true}
          title="Upload New Evidence"
          variant="INFO"
          actions={getEvidenceActions()}
        >
          <Input
            typeRepresentation="DRAGDROP"
            header="Drag & drop the prepared Object file here!"
            classes={{ root: "w-100 mb-4" }}
            onChange={handleEvidenceChange}
          />
          {state.attachedFile && (
            <SelectedFileCard
              file={state.attachedFile}
              onFileRemove={handleRemoveEvidence}
            />
          )}
        </NMPopup>
      )}
      {state.deletingFile && (
        <NMPopup
          visible={true}
          variant="CAUTION"
          title="Delete Attachment?"
          actions={getDeleteEvidenceActions()}
          onModalClose={() => updateState({ deletingFile: null })}
        >
          <Col className="w-100 fs-15">
            <span>
              Deletion log (Name & Datestamp) will be added automatically below
              the Filename.
            </span>
            <span>
              Filename will continue showing the Attachment list but it WILL NOT
              be possible to view or download.
            </span>
            <span className="mt-18 fs-12">
              <span className="text-caution fw-600">IMPORTANT!</span> This
              action CANNOT be reversed. If necessary, then the same File will
              need to be attached again.
            </span>
          </Col>
        </NMPopup>
      )}
    </Fragment>
  );
};
